import { c as _c } from "react/compiler-runtime";
import { type Message as AgUiMessage, type ToolCall as AgUiToolCall } from '@ag-ui/client';
import { CopilotActionCard } from '@precis-digital/kurama/MessageLayout';
import Typography from '@precis-digital/kurama/Typography';
import { type ReactElement } from 'react';
import ChangeUrlToolCallCard from 'shared/copilot/components/ToolCall/ChangeUrlToolCallCard';
import ConfirmActionCard from 'shared/copilot/components/ToolCall/ConfirmActionCard';
import DataSourceSuggestionCard from 'shared/copilot/components/ToolCall/DataSourceSuggestionCard';
import HighlightElementToolCallCard from 'shared/copilot/components/ToolCall/HighlightElementToolCallCard';
import PlanApprovalCard from 'shared/copilot/components/ToolCall/PlanApprovalCard';
import { useCopilot } from 'shared/context/CopilotContext';
import { getCopilotToolPresentation, getActionDetailContentForRegistry, parseToolCallArgumentsRecordForDisplay } from 'shared/copilot/toolRegistry';
import { type CopilotVisibleRenderKind } from 'shared/copilot/toolRegistry/types';
interface ToolCallProps {
  toolCall: AgUiToolCall;
  resultMessage?: AgUiMessage;
}
interface HighlightElementArguments {
  elementId?: string;
}
interface VisibleToolRenderContext {
  args: Record<string, unknown>;
  toolCall: AgUiToolCall;
  resultMessage?: AgUiMessage;
  title: string;
  toolName: string;
}
const HighlightElementToolCall = t0 => {
  const $ = _c(8);
  const {
    elementId
  } = t0;
  const {
    currentPageContext,
    highlightPageElement,
    scrollToPageElement
  } = useCopilot();
  const t1 = currentPageContext?.elements;
  let t2;
  if ($[0] !== elementId || $[1] !== highlightPageElement || $[2] !== scrollToPageElement) {
    t2 = () => {
      if (elementId == null) {
        return;
      }
      highlightPageElement(elementId);
      scrollToPageElement(elementId);
    };
    $[0] = elementId;
    $[1] = highlightPageElement;
    $[2] = scrollToPageElement;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== elementId || $[5] !== t1 || $[6] !== t2) {
    t3 = <HighlightElementToolCallCard elementId={elementId} elements={t1} onShowOnPage={t2} />;
    $[4] = elementId;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
};
const VISIBLE_TOOL_RENDERERS: Record<CopilotVisibleRenderKind, (context: VisibleToolRenderContext) => ReactElement> = {
  change_url: ({
    args,
    toolCall,
    resultMessage,
    toolName
  }) => <ChangeUrlToolCallCard args={args} toolCallId={toolCall.id} toolName={toolName === 'navigate_to_project' ? 'navigate_to_project' : 'change_url'} resultContent={resultMessage != null && 'content' in resultMessage ? resultMessage.content : undefined} />,
  highlight_element: ({
    args
  }) => {
    const highlightElementArgs = args as HighlightElementArguments;
    return <HighlightElementToolCall elementId={highlightElementArgs.elementId} />;
  },
  suggest_data_source: ({
    args
  }) => <DataSourceSuggestionCard args={args} />,
  confirm_action: ({
    args,
    toolCall,
    resultMessage,
    toolName
  }) => <ConfirmActionCard args={args} toolCallId={toolCall.id} toolName={toolName} resultContent={resultMessage != null && 'content' in resultMessage ? resultMessage.content : undefined} />,
  submit_plan_for_approval: ({
    args,
    toolCall,
    resultMessage,
    toolName
  }) => <PlanApprovalCard args={args} toolCallId={toolCall.id} toolName={toolName} resultContent={resultMessage != null && 'content' in resultMessage ? resultMessage.content : undefined} />,
  generic: ({
    title,
    toolName,
    args
  }) => <CopilotActionCard label={title} detail={getActionDetailContentForRegistry(toolName, args)} />
};
const renderRegisteredVisibleToolCall = (toolName: string, args: Record<string, unknown>, toolCall: AgUiToolCall, resultMessage?: AgUiMessage): ReactElement | null => {
  const presentation = getCopilotToolPresentation(toolName);
  if (presentation?.kind !== 'visible') {
    return null;
  }
  const render = VISIBLE_TOOL_RENDERERS[presentation.visibleRenderKind];
  return render({
    args,
    toolCall,
    resultMessage,
    title: presentation.title,
    toolName
  });
};

/**
 * Dispatches visible AG-UI tool calls to the matching ToolCall card component.
 */
const ToolCall = t0 => {
  const $ = _c(12);
  const {
    toolCall,
    resultMessage
  } = t0;
  let t1;
  let toolName;
  if ($[0] !== resultMessage || $[1] !== toolCall) {
    const visibleArguments = parseToolCallArgumentsRecordForDisplay(toolCall.function.arguments);
    toolName = toolCall.function.name;
    t1 = renderRegisteredVisibleToolCall(toolName, visibleArguments, toolCall, resultMessage);
    $[0] = resultMessage;
    $[1] = toolCall;
    $[2] = t1;
    $[3] = toolName;
  } else {
    t1 = $[2];
    toolName = $[3];
  }
  const handledToolCall = t1;
  if (handledToolCall != null) {
    return handledToolCall;
  }
  let t2;
  if ($[4] !== toolName) {
    t2 = getCopilotToolPresentation(toolName);
    $[4] = toolName;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const presentation = t2;
  let t3;
  if ($[6] !== presentation || $[7] !== toolName) {
    t3 = presentation?.kind === "visible" ? presentation.title : toolName.replaceAll("_", " ");
    $[6] = presentation;
    $[7] = toolName;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const label = t3;
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Typography variant="body3">Action completed</Typography>;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== label) {
    t5 = <CopilotActionCard label={label} detail={t4} />;
    $[10] = label;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
};
export default ToolCall;
export { DateRangePreview, FilterChipsPreview } from 'shared/copilot/toolRegistry';